/**
 * ParticipantTile Component - Individual video tile for participants
 *
 * Features:
 * - Video/audio display
 * - Name overlay
 * - Mute/video off indicators
 * - Speaking indicator
 * - Connection quality indicator
 */

import { useEffect, useRef, useState } from 'react';
import { Participant } from '../lib/session';

interface ParticipantTileProps {
  participant: Participant;
  stream?: MediaStream | null;
  isLocal?: boolean;
  isLarge?: boolean;
}

export function ParticipantTile({
  participant,
  stream,
  isLocal = false,
  isLarge = false
}: ParticipantTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [connectionQuality, _setConnectionQuality] = useState<'good' | 'fair' | 'poor'>('good');
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Speaking detection using Web Audio API
  useEffect(() => {
    if (!stream || participant.isMuted) {
      setIsSpeaking(false);
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    try {
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const checkSpeaking = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

        // Threshold for speaking detection
        setIsSpeaking(average > 30);
      };

      const interval = setInterval(checkSpeaking, 100);

      return () => {
        clearInterval(interval);
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
      };
    } catch (err) {
      console.error('[ParticipantTile] Audio analysis failed:', err);
    }
  }, [stream, participant.isMuted]);

  // Get initials for avatar
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get quality icon
  const getQualityIcon = () => {
    switch (connectionQuality) {
      case 'good':
        return '📶';
      case 'fair':
        return '📶';
      case 'poor':
        return '📵';
    }
  };

  return (
    <div
      className={`participant-tile ${isLarge ? 'large' : ''} ${isSpeaking ? 'speaking' : ''}`}
    >
      {/* Video or Avatar */}
      {participant.isVideoOff || !stream ? (
        <div className="video-off-avatar">
          <span className="avatar-initials">{getInitials(participant.name)}</span>
        </div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="participant-video"
        />
      )}

      {/* Speaking Indicator Border */}
      {isSpeaking && <div className="speaking-border"></div>}

      {/* Name Overlay */}
      <div className="tile-overlay">
        <div className="participant-info">
          <span className="participant-name">
            {participant.name}
            {isLocal && ' (You)'}
          </span>

          {/* Status Icons */}
          <div className="status-icons">
            {participant.isMuted && (
              <span className="status-icon muted" title="Muted">
                🔇
              </span>
            )}
            {participant.isVideoOff && (
              <span className="status-icon video-off" title="Camera Off">
                📷
              </span>
            )}
          </div>
        </div>

        {/* Role Badge */}
        {participant.role !== 'guest' && (
          <span className={`role-badge ${participant.role}`}>
            {participant.role === 'host' ? '👑 Host' : '🎬 Producer'}
          </span>
        )}

        {/* Connection Quality */}
        {!isLocal && (
          <span className={`connection-quality ${connectionQuality}`}>
            {getQualityIcon()}
          </span>
        )}
      </div>

      {/* Recording Indicator */}
      {participant.role === 'host' && (
        <div className="recording-badge">
          <span className="rec-dot"></span>
          REC
        </div>
      )}
    </div>
  );
}
